import React = require('react');
import { useEffect, useRef, useState } from 'react';
import { Field, InjectedFormProps } from 'redux-form';
import FieldFormControl from '../../../../../../components/FieldFormControl';
import { allowOnlyApostrophes, hasEmojis, hasSpecialCharacters, isRequired } from '../../../../../../util/validators';
import { Col, Row } from 'react-bootstrap';
import Select from 'react-select';
import { IAddress, IBilling, IChannel, ITraveller } from '../../../../../../data/api/models';
import { PostcodeLookup } from '../../../Components/PostcodeLookup';
import { get, head, map } from 'lodash';
import { TITLES } from '../../../../../../helpers/Quote';
import { YourRenewal } from '../YourRenewal';
import { renewalTypeSelectionEnum } from '../../../../components/YourRenewal';

interface IPaymentConsentProps extends InjectedFormProps {
  permissions: boolean;
  travellers: ITraveller[];
  channel: IChannel;
  billing: IBilling;
  shouldShowRenewalSection: boolean;
  renewalStatus: string;
  ignorePermissions?: boolean;

  change(field: string, value: any): void;
}

export const PaymentConsent = ({
                                 permissions, billing, travellers, change, channel, shouldShowRenewalSection, ignorePermissions = false, ...props
                               }: IPaymentConsentProps) => {

  const leadTraveller = head(travellers);
  const title = get(leadTraveller, 'title', '');
  const leadAddress = get(head(travellers), 'address');
  const fullName = (title ? title : '') + ' ' + get(leadTraveller, 'firstName', '') + ' ' + get(leadTraveller, 'lastName', '')

  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const [showSetting, setShowSetting] = useState(false);
  const [showCardHolderName, setShowCardHolderName] = useState({ label: fullName, value: false });
  const [showBillingAddress, setShowBillingAddress] = useState({ label: 'Same as home address', value: false });
  const previousValues = usePrevious({ showCardHolderName, showBillingAddress });

  useEffect(() => {
    const address: IAddress = get(leadTraveller, 'address');
    setCardHolderName(leadTraveller);
    setBillingAddress(address);
  }, [])

  useEffect(() => {
    setShowCardHolderName({ label: fullName, value: false })
    setCardHolderName(leadTraveller);
  }, [head(travellers)])

  useEffect(() => {
    setBillingAddress(leadAddress);
  }, [leadAddress])

  useEffect(() => {
    if (previousValues && get(previousValues, 'showBillingAddress.value') !== showBillingAddress.value) {
      if (!showBillingAddress.value) {
        const address: IAddress = get(leadTraveller, 'address');
        change('reuseCustomerAddress', true)
        setBillingAddress(address);
      }

      if (showBillingAddress.value) {
        change('reuseCustomerAddress', false)
        setBillingAddress();
      }
    }

    if (previousValues && get(previousValues, 'showCardHolderName.value') !== showCardHolderName.value) {
      if (!showCardHolderName.value) {
        setCardHolderName(leadTraveller)
      }

      if (showCardHolderName.value) {
        setCardHolderName()
      }
    }

  }, [showBillingAddress, showCardHolderName]);

  const optionsName = [
    { label: fullName, value: false },
    { label: 'Other', value: true }
  ]

  const addressName = [
    { label: 'Same as home address', value: false },
    { label: 'Other', value: true }
  ]

  const setCardHolderName = (traveller?: ITraveller) => {
    change(`billing.title`, get(traveller, 'title', null));
    change(`billing.firstName`, get(traveller, 'firstName', null));
    change(`billing.lastName`, get(traveller, 'lastName', null));
  }

  const setBillingAddress = (address?: IAddress) => {
    change('billing.address.postcode', get(address, 'postcode', null));
    change('billing.address.line1', get(address, 'line1', null));
    change('billing.address.line2', get(address, 'line2', null));
    change('billing.address.county', get(address, 'county', null));
    change('billing.address.country', get(address, 'country', null));
    change('billing.address.town', get(address, 'town', null));
  }

  const changePaymentSetting = () => {
    setShowSetting(!showSetting)
  }

  const onChangeCardholder = (value) => {
    setShowCardHolderName(value)
  }

  const onChangeAddress = (value) => {
    setShowBillingAddress(value)
  }

  return (
    <div className={'payment-details-container'}>
      <div className={'permissions-container'}>
        <div>Payment: We will securely save your payment card so payments on your policy can be handled quicker and
          easier– this includes any future changes including policy renewal. You can cancel this by contacting us at any
          time or clicking <span
            className={'payment-details-setting'} onClick={changePaymentSetting}>here</span>.
        </div>
        {showSetting && (
          <div className={'card-preferences-container'}>
            <div className="card-preferences-selection-container">
              <div className={permissions ? 'box selected-trip-type' : 'box'}>
                <Field
                  name={'permissions'}
                  type="radio"
                  value={true}
                  complexValue={true}
                  component={FieldFormControl}
                  validate={[isRequired]}
                >
                  <div className={`trip-type-btn`}>
                    <div className={'trip-type-selection-container'}>
                      <div className={'title-selection'}>Save the card details</div>
                    </div>
                  </div>
                </Field>
              </div>
            </div>
            <div className="card-preferences-selection-container">
              <div className={!permissions ? 'box selected-trip-type' : 'box'}>
                <Field
                  name={'permissions'}
                  type="radio"
                  value={false}
                  complexValue={true}
                  component={FieldFormControl}
                  validate={[isRequired]}
                >
                  <div className={`trip-type-btn`}>
                    <div className={'trip-type-selection-container'}>
                      <div className={'title-selection'}>{'Do not save the card details'}</div>
                      {!permissions && (
                        <div className={'sub-text'}>
                          {'You will need to give us your payment details every time you make a change or when you’d like to renew with us'}
                        </div>
                      )}
                    </div>
                  </div>
                </Field>
              </div>
            </div>
          </div>
        )}
      </div>
      <>
        {shouldShowRenewalSection && (
          <YourRenewal
            formPath={'renewalStatus'}
            renewalStatus={props.renewalStatus}
            ignorePermissions={ignorePermissions}
            change={change}
            defaultSelected={renewalTypeSelectionEnum.AUTO}
            allowAutoRenewal={permissions}
            {...props}/>
        )}
      </>
      <div className={'details-selection-container'}>
        <div className={'title'}><img src={require('../../../../img/policy-administrator.svg')}
                                      alt="Admiral"/> Cardholder details
        </div>
        <div>
          <Row className={'select'}>
            <Col xs={6} className={'select-question full-width-xs'}>Who does the card belong to?</Col>
            <Col xs={6} className={'full-width-xs'}><Select
              value={showCardHolderName.value}
              onChange={onChangeCardholder}
              clearable={false}
              options={optionsName}
            /></Col>
          </Row>
          {showCardHolderName.value && (
            <>
              <Row>
                <Col xs={6} className={'full-width-xs'}>Your preferred title</Col>
                <Col xs={6} className={'full-width-xs title-selection-container'}>
                  {map(TITLES, (value, key) => {
                    const isSelected = billing.title === value;
                    return (
                      <Col xs={4}
                           className={isSelected ? 'traveller-title-selection selected' : 'traveller-title-selection'}
                           key={key}>
                        <div>
                          <Field
                            name={`billing.title`}
                            id={key + '-btn'}
                            type="radio"
                            value={value}
                            component={FieldFormControl}
                            validate={[isRequired]}
                          >
                            <div className={`trip-type-btn ${key}`}>
                              <div className={'trip-type-selection-container'}>
                                {value}
                              </div>
                            </div>
                          </Field>
                        </div>
                      </Col>
                    )
                  })}
                </Col>
              </Row>
              <Row className={'adm-form-group'}>
                <Col xs={6} className={'full-width-xs'}>First Name on the card</Col>
                <Col xs={6} className={'full-width-xs'}>
                  <Field
                    name={`billing.firstName`}
                    type="text"
                    component={FieldFormControl}
                    validate={[isRequired, hasEmojis, hasSpecialCharacters]}
                    autocomplete="new-password"
                  />
                </Col>
              </Row>
              <Row className={'adm-form-group'}>
                <Col xs={6} className={'full-width-xs'}>Last Name on the card</Col>
                <Col xs={6} className={'full-width-xs'}>
                  <Field
                    name={`billing.lastName`}
                    type="text"
                    component={FieldFormControl}
                    validate={[isRequired, hasEmojis, allowOnlyApostrophes]}
                    autocomplete="new-password"
                  />
                </Col>
              </Row>
            </>
          )}
          <Row className={'select'}>
            <Col xs={6} className={'select-question full-width-xs'}>What's their billing address?</Col>
            <Col xs={6} className={'full-width-xs'}><Select
              value={showBillingAddress.value}
              onChange={onChangeAddress}
              clearable={false}
              options={addressName}
            /></Col>
          </Row>
        </div>
        <div>
          <>
            {showBillingAddress.value && (
              <PostcodeLookup basePath={'billing.address'}
                              change={change}
                              travellers={travellers}
                              channel={channel}
                              title={'Address'}
                              {...props}/>
            )}
          </>
        </div>
      </div>
    </div>
  )
}
